import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

const COOKIE_KEY = "AppUserState";
export const LOCALSTORE_KEY = "AppUserStateTrigger";

export const appUserInitialState: AppUserState = {
  accessToken: "",
  accessTokenExpiry: "",
  refreshToken: "",
  refreshTokenExpiry: "",
  sessionExpired: true,
};

export const isAppUserState = (value: unknown): value is AppUserState => {
  if (!value || typeof value !== "object") return false;

  const state = value as Record<string, unknown>;
  return (
    typeof state.accessToken === "string" &&
    typeof state.accessTokenExpiry === "string" &&
    typeof state.refreshToken === "string" &&
    typeof state.refreshTokenExpiry === "string" &&
    typeof state.sessionExpired === "boolean"
  );
};

const saveState = (state: AppUserState, expires: Date) => {
  localStorage.setItem(LOCALSTORE_KEY, state.accessTokenExpiry); // Triggers sync with other tabs
  Cookies.set(COOKIE_KEY, JSON.stringify(state), {
    secure: true,
    sameSite: "strict",
    path: "/",
    expires,
  });
};

export const loadState = (): AppUserState => {
  try {
    const cookieValue = Cookies.get(COOKIE_KEY);
    const parsedValue = cookieValue ? JSON.parse(cookieValue) : {};
    const result: AppUserState = { ...appUserInitialState };

    if (isAppUserState(parsedValue)) {
      result.accessToken = parsedValue.accessToken;
      result.accessTokenExpiry = parsedValue.accessTokenExpiry;
      result.refreshToken = parsedValue.refreshToken;
      result.refreshTokenExpiry = parsedValue.refreshTokenExpiry;
      result.sessionExpired = parsedValue.sessionExpired;
    }
    return result;
  } catch {
    return appUserInitialState;
  }
};

export const clearAppUserState = () => {
  Cookies.remove(COOKIE_KEY, { path: "/" });
};

export const hasUserStateCookie = (): boolean => Boolean(Cookies.get(COOKIE_KEY));

export interface AppUserState {
  accessToken: string;
  accessTokenExpiry: string;
  refreshToken: string;
  refreshTokenExpiry: string;
  sessionExpired: boolean;
}

export const appUserSlice = createSlice({
  name: "appUser",
  initialState: loadState(),
  reducers: {
    setTokens: (
      state,
      action: PayloadAction<
        Pick<AppUserState, "accessToken" | "refreshToken" | "accessTokenExpiry" | "refreshTokenExpiry">
      >
    ) => {
      state.accessToken = action.payload.accessToken;
      state.accessTokenExpiry = action.payload.accessTokenExpiry;
      state.refreshToken = action.payload.refreshToken;
      state.refreshTokenExpiry = action.payload.refreshTokenExpiry;
      state.sessionExpired = false;
      saveState(state, new Date(state.refreshTokenExpiry));
    },
    setSessionExpired: (state) => {
      // resets to initial state
      Object.assign(state, appUserInitialState);
      clearAppUserState();
    },
  },
});
